import { useState, useMemo, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { ProductCard } from '@/components/ProductCard';
import { ProductFilters } from '@/components/ProductFilters';
import { productos, normalizarPresentacion, categorias } from '@/data/products';
import { useSeo } from '@/hooks/useSeo';
import { productToAnalytics, trackEcommerce } from '@/utils/analytics';

export function TiendaPage() {
  useSeo({
    title: 'Tienda de Productos Naturales y Andinos | Huaytanatura',
    description: 'Explora nuestro catálogo: granos andinos, harinas sin gluten, frutos secos, legumbres, aceites y superalimentos 100% naturales. Pedidos por WhatsApp.',
    path: '/tienda',
  });

  const [searchParams] = useSearchParams();
  const categoriaInicial = searchParams.get('categoria');
  const categoriaValida = categoriaInicial && categorias.includes(categoriaInicial)
    ? categoriaInicial
    : null;

  const [searchQuery, setSearchQuery] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    categorias: categoriaValida ? [categoriaValida] : [],
    etiquetas: [],
    presentaciones: [],
    ordenar: 'relevancia'
  });

  // Sincroniza el filtro cuando se llega desde las tarjetas de categorías del home
  useEffect(() => {
    if (categoriaValida) {
      setFilters((prev) =>
        prev.categorias.includes(categoriaValida)
          ? prev
          : { ...prev, categorias: [categoriaValida] }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriaValida]);

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = productos;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(query) ||
        product.descripcion.toLowerCase().includes(query) ||
        product.etiquetas.some(tag => tag.toLowerCase().includes(query)) ||
        product.categoria.toLowerCase().includes(query)
      );
    }

    // Apply category filter
    if (filters.categorias.length > 0) {
      filtered = filtered.filter(product => 
        filters.categorias.includes(product.categoria)
      );
    }

    // Apply tags filter
    if (filters.etiquetas.length > 0) {
      filtered = filtered.filter(product => 
        filters.etiquetas.some(tag => product.etiquetas.includes(tag))
      );
    }

    // Presentaciones filter: compara formatos normalizados (250g, 500ml, 1L, ...)
    if (filters.presentaciones.length > 0) {
      filtered = filtered.filter(product =>
        product.presentaciones.some(pres => {
          const formato = normalizarPresentacion(pres.presentacion);
          return formato !== null && filters.presentaciones.includes(formato);
        })
      );
    }

    // Apply sorting
    switch (filters.ordenar) {
      case 'precio-asc':
        filtered.sort((a, b) => {
          const minPriceA = Math.min(...a.presentaciones.map(p => p.precio));
          const minPriceB = Math.min(...b.presentaciones.map(p => p.precio));
          return minPriceA - minPriceB;
        });
        break;
      case 'precio-desc':
        filtered.sort((a, b) => {
          const minPriceA = Math.min(...a.presentaciones.map(p => p.precio));
          const minPriceB = Math.min(...b.presentaciones.map(p => p.precio));
          return minPriceB - minPriceA;
        });
        break;
      case 'nombre':
        filtered.sort((a, b) => a.name.localeCompare(b.name, 'es'));
        break;
      default:
        // Keep original order (relevancia)
        break;
    }

    return filtered;
  }, [searchQuery, filters]);

  const listaVisible = filteredAndSortedProducts.map((product) => product.id).join('|');
  const nombreLista = filters.categorias[0] || (searchQuery.trim() ? 'Búsqueda' : 'Catálogo');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      trackEcommerce(
        'view_item_list',
        filteredAndSortedProducts.map((product, index) => ({
          ...productToAnalytics(
            product,
            product.presentaciones[0].presentacion,
            product.presentaciones[0].precio,
          ),
          item_list_name: nombreLista,
          index,
        })),
        { item_list_name: nombreLista },
      );
    }, 400);
    return () => window.clearTimeout(timer);
  }, [listaVisible, nombreLista, filteredAndSortedProducts]);

  return (
    <div className="min-h-screen bg-background">
      {/* Banner */}
      <div className="bg-primary/5 border-b border-border texture-paper">
        <div className="container mx-auto px-4 py-4">
          <div className="text-center">
            <p className="text-sm text-primary font-medium">
              🌿 Huaytanatura · 100% naturales · 🚚 Entregas en Ñaña, Chaclacayo y Chosica · 📱 Pedidos por WhatsApp
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Nuestra Tienda Natural
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            Descubre productos naturales cuidadosamente seleccionados para toda tu familia
          </p>

          {/* Search Bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              type="text"
              placeholder="Buscar productos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="lg:w-64 flex-shrink-0">
            <ProductFilters
              filters={filters}
              onFiltersChange={setFilters}
              isOpen={filtersOpen}
              onToggle={() => setFiltersOpen(!filtersOpen)}
            />
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Results Info */}
            <div className="mb-6">
              <p className="text-muted-foreground">
                {filteredAndSortedProducts.length === productos.length
                  ? `Mostrando todos los ${productos.length} productos`
                  : `Mostrando ${filteredAndSortedProducts.length} de ${productos.length} productos`
                }
                {searchQuery && (
                  <span> para "{searchQuery}"</span>
                )}
              </p>
            </div>

            {/* Products Grid */}
            {filteredAndSortedProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                {filteredAndSortedProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No se encontraron productos
                </h3>
                <p className="text-muted-foreground mb-4">
                  Intenta ajustar tus filtros o términos de búsqueda
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilters({
                      categorias: [],
                      etiquetas: [],
                      presentaciones: [],
                      ordenar: 'relevancia'
                    });
                  }}
                  className="text-primary hover:underline"
                >
                  Limpiar todos los filtros
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
